import { motion, AnimatePresence } from "motion/react"
import { type ReactNode } from "react"
import { Check } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { useOnboarding, type OnboardingStep } from "@/lib/onboarding/onboarding-context"

// Spring physics — gives the wizard a more deliberate, alive feel than a flat
// duration curve. Used for content slide + progress fill.
export const ONBOARDING_SPRING = { type: "spring", stiffness: 260, damping: 30, mass: 0.9 } as const

// Labels are per-org-type because the same step name carries different
// meaning across flows. For groups, `extra` is "Dealerships" and we add a
// `bodyshops` step after it; for non-groups, `extra` is the body-shop /
// fees / connect step rendered as "Setup," and `bodyshops` is hidden.
function getStepLabel(step: OnboardingStep, isGroup: boolean): string {
  if (isGroup) {
    if (step === "extra") return "Dealerships"
    if (step === "bodyshops") return "Body shops"
  } else {
    if (step === "invite") return "Team"
    if (step === "extra") return "Setup"
  }
  if (step === "personal") return "Welcome"
  if (step === "company") return "Company"
  if (step === "location") return "Where"
  return "" // hidden step, never rendered
}

const STEP_ORDER: OnboardingStep[] = [
  "personal",
  "company",
  "location",
  "invite",
  "extra",
  "bodyshops",
]

/* ── Shell ────────────────────────────────────────────── */

export function OnboardingShell({ children }: { children: ReactNode }) {
  const { step, direction } = useOnboarding()

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-zinc-50 to-white">
      {/* Refined backdrop — single soft glow + dot grid texture */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-[-200px] size-[680px] rounded-full bg-emerald-200/30 blur-3xl" />
        <div className="absolute top-1/2 -left-40 size-[440px] rounded-full bg-zinc-100/60 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(228 228 231 / 0.55) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="relative flex min-h-screen flex-col items-center justify-center p-4 md:p-10">
        <div className="w-full max-w-5xl">
          <Header />

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              initial={{ opacity: 0, y: 18, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -18, scale: 0.985 }}
              transition={{ ...ONBOARDING_SPRING, opacity: { duration: 0.25 } }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

/* ── Header (logo + numbered step rail) ──────────────── */

function Header() {
  const { stepIndex, selectedType } = useOnboarding()

  // Each flow visits a different subset of stops. Groups skip `invite`
  // (they never go through it — see onboarding-context.goNext); non-groups
  // skip `bodyshops` (added for the group flow only). Filtering before
  // mapping keeps the rail honest about what the user will actually see.
  const isGroup = selectedType === "DealershipGroup"
  const visibleStepOrder: OnboardingStep[] = STEP_ORDER.filter((s) => {
    if (isGroup && s === "invite") return false
    if (!isGroup && s === "bodyshops") return false
    return true
  })
  const currentStepName = STEP_ORDER[stepIndex] ?? "personal"
  const visibleActiveIndex = visibleStepOrder.indexOf(currentStepName)

  return (
    <div className="mb-6 md:mb-8">
      <div className="flex items-center justify-center mb-7">
        <div className="inline-flex items-center gap-3">
          <div className="size-12 rounded-2xl bg-zinc-900 grid place-items-center shadow-lg shadow-zinc-900/15">
            <span
              className="text-white font-black text-sm"
              style={{ fontFamily: "var(--font-syne)" }}
            >
              eT<span className="text-emerald-400">X</span>
            </span>
          </div>
          <span
            className="text-base font-bold text-zinc-900 tracking-tight hidden sm:inline"
            style={{ fontFamily: "var(--font-syne)" }}
          >
            Impa<span className="text-emerald-500">X</span>t
          </span>
        </div>
      </div>

      {/* Numbered step rail — wider max for 5 stops so labels never wrap on
          a narrow laptop. The rail itself stays centered. */}
      <ol className="flex items-center justify-between max-w-2xl mx-auto px-2">
        {visibleStepOrder.map((s, i) => {
          const isActive = i === visibleActiveIndex
          const isDone = i < visibleActiveIndex
          const isLast = i === visibleStepOrder.length - 1

          return (
            <li key={s} className={cx("flex items-center", !isLast && "flex-1")}>
              <div className="flex flex-col items-center gap-2 shrink-0">
                <motion.div
                  initial={false}
                  animate={{
                    backgroundColor: isDone
                      ? "rgb(16 185 129)"
                      : isActive
                        ? "rgb(255 255 255)"
                        : "rgb(255 255 255)",
                    borderColor: isDone
                      ? "rgb(16 185 129)"
                      : isActive
                        ? "rgb(16 185 129)"
                        : "rgb(228 228 231)",
                    color: isDone
                      ? "rgb(255 255 255)"
                      : isActive
                        ? "rgb(16 185 129)"
                        : "rgb(161 161 170)",
                    scale: isActive ? 1.06 : 1,
                  }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="relative size-9 rounded-full border-2 grid place-items-center text-[13px] font-bold tabular-nums"
                  style={{
                    boxShadow: isActive ? "0 0 0 4px rgba(16,185,129,0.12)" : "none",
                  }}
                >
                  {isDone ? (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 25 }}
                    >
                      <Check className="size-4" strokeWidth={3} aria-hidden />
                    </motion.span>
                  ) : (
                    i + 1
                  )}
                </motion.div>
                <span
                  className={cx(
                    "text-[11px] md:text-xs font-semibold uppercase tracking-wider transition-colors duration-300",
                    isActive
                      ? "text-zinc-900"
                      : isDone
                        ? "text-emerald-600"
                        : "text-zinc-400",
                  )}
                >
                  {getStepLabel(s, isGroup)}
                </span>
              </div>
              {!isLast && (
                <div className="flex-1 mx-2 md:mx-3 -mt-5 h-0.5 bg-zinc-200/80 relative overflow-hidden rounded-full">
                  <motion.div
                    initial={false}
                    animate={{ scaleX: isDone ? 1 : 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    style={{ originX: 0 }}
                    className="absolute inset-0 bg-emerald-500"
                  />
                </div>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

/* ── Two-column step layout ──────────────────────────── */

export function StepLayout({
  eyebrow,
  title,
  subtitle,
  hero,
  children,
}: {
  eyebrow?: string
  title: string
  subtitle: string
  hero: ReactNode
  children: ReactNode
}) {
  return (
    <div className="rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.07] overflow-hidden">
      <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        {/* Form column */}
        <div className="p-7 md:p-10 lg:p-12">
          <div className="mb-7">
            {eyebrow && (
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
                {eyebrow}
              </p>
            )}
            <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
              {title}
            </h2>
            <p className="text-[15px] text-zinc-500 mt-2 leading-relaxed">{subtitle}</p>
          </div>
          {children}
        </div>

        {/* Hero column */}
        <div className="relative hidden lg:block bg-gradient-to-br from-zinc-50 via-white to-emerald-50/40 border-l border-zinc-100 p-10 overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-50"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgb(212 212 216 / 0.6) 1px, transparent 0)",
              backgroundSize: "16px 16px",
            }}
          />
          <div className="relative h-full flex items-center justify-center">{hero}</div>
        </div>
      </div>
    </div>
  )
}

/* ── Form primitives ─────────────────────────────────── */

export const onboardingInputClass =
  "w-full h-12 rounded-xl border border-zinc-200 bg-white px-4 text-[15px] text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all duration-150"

export const onboardingInputErrorClass =
  "border-red-300 focus:border-red-500 focus:ring-red-500/10"

export const onboardingInputDisabledClass =
  "w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 px-4 text-[15px] text-zinc-500 outline-none cursor-not-allowed"

export function Field({
  label,
  error,
  hint,
  required,
  children,
}: {
  label: string
  error?: string
  hint?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-1 text-[13px] font-semibold text-zinc-700">
        {label}
        {required && (
          <span className="text-red-500" aria-hidden>
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="text-[12px] text-zinc-400">{hint}</p>}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[12px] font-medium text-red-600"
        >
          {error}
        </motion.p>
      )}
    </div>
  )
}

/* ── Buttons ─────────────────────────────────────────── */

export function PrimaryButton({
  children,
  loading,
  disabled,
  type = "submit",
  onClick,
  variant = "dark",
  size = "default",
}: {
  children: ReactNode
  loading?: boolean
  disabled?: boolean
  type?: "submit" | "button"
  onClick?: () => void
  variant?: "dark" | "emerald"
  size?: "default" | "wide"
}) {
  return (
    <motion.button
      whileHover={!disabled && !loading ? { y: -1 } : undefined}
      whileTap={!disabled && !loading ? { scale: 0.98 } : undefined}
      transition={{ duration: 0.15 }}
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={cx(
        "h-12 rounded-xl text-[15px] font-semibold flex items-center justify-center gap-2",
        "shadow-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none",
        "transition-shadow duration-200",
        size === "wide" ? "w-full" : "px-7",
        variant === "dark"
          ? "bg-zinc-900 hover:bg-black text-white shadow-zinc-900/20 hover:shadow-zinc-900/30"
          : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/25 hover:shadow-emerald-600/40",
      )}
    >
      {loading ? (
        <span className="size-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : (
        children
      )}
    </motion.button>
  )
}

export function GhostButton({
  children,
  onClick,
  disabled,
  type = "button",
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  type?: "submit" | "button"
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="h-11 px-4 text-[14px] font-semibold text-zinc-500 hover:text-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
    >
      {children}
    </button>
  )
}

export function BackButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="h-12 px-5 rounded-xl text-[14px] font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 flex items-center gap-1.5"
    >
      Back
    </button>
  )
}

/* ── Step footer (Back · Skip · Continue) ────────────── */

export function StepActions({
  onBack,
  primary,
  skip,
}: {
  onBack?: () => void
  primary: ReactNode
  skip?: { onSkip: () => void; disabled?: boolean; label?: string }
}) {
  // Skip is rendered as a real outlined button now (was a ghost). Onboarding
  // users were missing it entirely as a low-contrast text-only control —
  // the bordered button puts it on the same visual tier as Back without
  // competing with the primary CTA on the right.
  return (
    <div className="flex items-center justify-between gap-3 pt-2">
      <div className="flex items-center gap-2">
        {onBack && <BackButton onClick={onBack} />}
        {skip && (
          <button
            type="button"
            onClick={skip.onSkip}
            disabled={skip.disabled}
            className="h-12 px-5 rounded-xl border border-zinc-200 bg-white text-[14px] font-semibold text-zinc-700 whitespace-nowrap hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
          >
            {skip.label ?? "Skip for now"}
          </button>
        )}
      </div>
      <div className="shrink-0">{primary}</div>
    </div>
  )
}

/* ── Staggered field reveal ──────────────────────────── */

const FIELD_STAGGER = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1], delay: 0.1 + i * 0.06 },
  }),
}

export function StaggerField({ index, children }: { index: number; children: ReactNode }) {
  return (
    <motion.div custom={index} initial="hidden" animate="show" variants={FIELD_STAGGER}>
      {children}
    </motion.div>
  )
}
